import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBuilding, faWifi, faCommentDots, faQrcode,
  faArrowRight, faChevronRight, faGear, faCreditCard
} from '@fortawesome/free-solid-svg-icons';
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';

const StatCard = ({ icon, iconClass, label, value, loading }) => (
  <div className="stat-card">
    <div className={iconClass}>
      <FontAwesomeIcon icon={icon} />
    </div>
    <div>
      <p className="text-sm text-secondary-500 font-medium">{label}</p>
      <p className="text-2xl font-bold text-secondary-900">
        {loading ? <span className="inline-block w-10 h-6 bg-secondary-200 rounded animate-pulse" /> : value}
      </p>
    </div>
  </div>
);

const QuickAction = ({ icon, iconBg, title, desc, href }) => (
  <a
    href={href}
    className="flex items-center gap-4 p-4 bg-white rounded-2xl border border-secondary-100 hover:border-primary-200 hover:shadow-md transition-all duration-200 group"
  >
    <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${iconBg}`}>
      <FontAwesomeIcon icon={icon} className="text-white text-sm" />
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-sm font-semibold text-secondary-800">{title}</p>
      <p className="text-xs text-secondary-400 truncate">{desc}</p>
    </div>
    <FontAwesomeIcon icon={faChevronRight} className="text-secondary-300 group-hover:text-primary-500 transition-colors text-xs" />
  </a>
);

const SimpleOverview = ({ userInfo }) => {
  const [stats, setStats] = useState({ totalSubaccounts: 0, activeConnections: 0, messagesToday: 0, qrCodesGenerated: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (userInfo?.agency?.id) fetchStats();
  }, [userInfo]);

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`/agencies/${userInfo.agency.id}/stats`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      if (response.ok) {
        const data = await response.json();
        setStats({
          totalSubaccounts: data.usage?.locations?.current || 0,
          activeConnections: data.stats?.connectedWhatsApp || 0,
          messagesToday: data.stats?.totalMessages || 0,
          qrCodesGenerated: data.stats?.qrCodesGenerated || 0,
        });
      }
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };

  const firstName = userInfo?.user?.firstName || userInfo?.firstName || 'there';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-secondary-900">Hey {firstName}, here's your overview</h1>
        <p className="mt-1 text-sm text-secondary-500">Your GHLWA workspace at a glance.</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard icon={faBuilding}    iconClass="stat-icon-indigo"  label="Linked Locations"    value={stats.totalSubaccounts}  loading={loading} />
        <StatCard icon={faWifi}        iconClass="stat-icon-blue"   label="Live Channels"       value={stats.activeConnections} loading={loading} />
        <StatCard icon={faCommentDots} iconClass="stat-icon-yellow" label="Messages Routed"     value={stats.messagesToday}     loading={loading} />
        <StatCard icon={faQrcode}      iconClass="stat-icon-teal"   label="QR Codes Generated"  value={stats.qrCodesGenerated}  loading={loading} />
      </div>

      {/* Quick actions */}
      <div>
        <h2 className="text-base font-semibold text-secondary-700 mb-3">Shortcuts</h2>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <QuickAction icon={faBuilding}   iconBg="bg-primary-500"    title="Manage Subaccounts" desc="View and configure your linked locations"   href="/dashboard/subaccounts" />
          <QuickAction icon={faWhatsapp}   iconBg="bg-green-500"      title="Connect WhatsApp"   desc="Pair a new WhatsApp number to a location"  href="/dashboard/subaccounts" />
          <QuickAction icon={faQrcode}     iconBg="bg-accent-blue"    title="Generate QR Code"   desc="Produce a scannable WhatsApp QR"           href="/dashboard/subaccounts" />
          <QuickAction icon={faCreditCard} iconBg="bg-accent-yellow"  title="Billing"            desc="Review your plan and payment history"       href="/dashboard/billing" />
          <QuickAction icon={faGear}       iconBg="bg-secondary-500"  title="Settings"           desc="Adjust preferences and account details"    href="/dashboard/settings" />
        </div>
      </div>

      {/* Getting started banner - shown when no connections */}
      {!loading && stats.activeConnections === 0 && (
        <div className="rounded-2xl p-6 flex items-start gap-4" style={{ background: 'linear-gradient(135deg, #1e1b4b 0%, #4338ca 100%)' }}>
          <div className="w-10 h-10 bg-white/15 rounded-xl flex items-center justify-center flex-shrink-0">
            <FontAwesomeIcon icon={faWhatsapp} className="text-white" />
          </div>
          <div className="flex-1">
            <h3 className="text-base font-semibold text-white mb-1">Link your first WhatsApp channel</h3>
            <p className="text-indigo-200 text-sm mb-4">Add a location and scan a QR code to start routing messages in under 2 minutes.</p>
            <a href="/dashboard/subaccounts" className="inline-flex items-center gap-2 bg-white text-indigo-900 font-semibold text-sm px-4 py-2 rounded-xl hover:bg-indigo-50 transition-colors">
              Start now <FontAwesomeIcon icon={faArrowRight} className="text-xs" />
            </a>
          </div>
        </div>
      )}
    </div>
  );
};

export default SimpleOverview;
